import { Expose } from 'class-transformer';
import { PccAuditInfo } from './audit-info.typings';

/**
 * Represents a care team location for a practitioner
 * @see CareTeamLocation schema in PCC API
 */
export class PccCareTeamLocation {
	/**
	 * Street address for the care team location
	 */
	address1 = '';

	/**
	 * Additional address information for the care team location
	 */
	address2 = '';

	/**
	 * City for the care team location
	 */
	city = '';

	/**
	 * State/Province for the care team location
	 */
	state = '';

	/**
	 * County for the care team location
	 */
	county = '';

	/**
	 * Country for the care team location
	 */
	country = '';

	/**
	 * Postal Code for the care team location
	 */
	postalCode = '';

	constructor(partial?: Partial<PccCareTeamLocation>) {
		if (partial) Object.assign(this, partial);
	}
}

/**
 * Represents a practitioner/care provider from PointClickCare
 * Response from: GET /orgs/{orgUuid}/practitioners?patientId={patientId}
 * @see Practitioner schema in PCC API
 */
export class PccPractitioner extends PccAuditInfo {
	/**
	 * Unique identifier for the practitioner within the organization
	 */
	practitionerId = 0;

	/**
	 * The practitioner's first name
	 */
	firstName = '';

	/**
	 * The practitioner's last name
	 */
	lastName = '';

	/**
	 * The practitioner's middle name
	 */
	middleName = '';

	/**
	 * The practitioner's title, if available
	 */
	title = '';

	/**
	 * The practitioner's gender. Possible responses are 'M', 'F' or 'U'
	 */
	gender = '';

	/**
	 * Type of medical professional (e.g. physician, dentist, therapist)
	 */
	providerType = '';

	/**
	 * Indicates if primary practitioner. Value is "Primary" for primary practitioner only
	 */
	relation = '';

	/**
	 * National Provider ID for the practitioner
	 */
	npi = '';

	/**
	 * Group National Provider ID for the practitioner, if available
	 */
	groupNpi = '';

	/**
	 * Unique physician identification number (UPIN). US facilities only
	 */
	upin = '';

	/**
	 * The practitioner's registration code
	 */
	registrationCode = '';

	/**
	 * Taxonomy Code for the medical professional
	 */
	taxonomyCode = '';

	/**
	 * Description for taxonomy code
	 */
	taxonomyDescription = '';

	/**
	 * Email address for the practitioner, if available
	 */
	email = '';

	/**
	 * Office phone number for the practitioner, if available
	 */
	officePhone = '';

	/**
	 * Home phone number for the practitioner, if available
	 */
	homePhone = '';

	/**
	 * Cell phone number for the practitioner, if available
	 */
	cellPhone = '';

	/**
	 * Fax number for the practitioner, if available
	 */
	fax = '';

	/**
	 * Pager number for the practitioner, if available
	 */
	pager = '';

	/**
	 * Street address for the practitioner's address, if available
	 */
	addressLine1 = '';

	/**
	 * Additional address information for the practitioner's address, if available
	 */
	addressLine2 = '';

	/**
	 * City for the practitioner's address, if available
	 */
	city = '';

	/**
	 * State/Province for the practitioner's address, if available
	 */
	state = '';

	/**
	 * County for the practitioner's address, if available
	 */
	county = '';

	/**
	 * Country for the practitioner's address, if available
	 */
	country = '';

	/**
	 * Postal Code for the practitioner's address, if available
	 */
	postalCode = '';

	/**
	 * Care team location information
	 */
	careTeamLocation: PccCareTeamLocation;

	@Expose()
	get fullName(): string {
		return [this.title, this.firstName, this.middleName, this.lastName].filter((val) => val).join(' ');
	}

	@Expose()
	get fullAddress(): string {
		return [this.addressLine1, this.city, this.state, this.postalCode].filter((val) => val).join(', ');
	}

	@Expose()
	get phone(): string {
		return this.officePhone || this.cellPhone || this.homePhone || '';
	}

	constructor(partial?: Partial<PccPractitioner>) {
		super();
		if (partial) Object.assign(this, partial);
		if (this.careTeamLocation) this.careTeamLocation = new PccCareTeamLocation(this.careTeamLocation);
	}
}
